import PropTypes from 'prop-types';
import cn from 'classnames';

import DesktopTabButtons from './DesktopTabButtons.jsx';
import MobileTabButtons from './MobileTabButtons.jsx';
import {
  useLayoutEffect,
  useState,
  useId,
  useEffect,
  useRef
} from 'react';

// JSDoc type definitions
/**
 * @typedef {Object} LdsTabsProps
 * @property {number|string} [activeTab] - Current active tab value
 * @property {string} [ariaLabel] - Optional property to set aria-label on the tab group
 * @property {string} [tabAlign] - Property to set the alignment of the tabs. Options are "align-left", "align-center", and "align-right"
 * @property {string} [tabClass] - Property to style of the tab group. Options are "solid" or "underlined"
 * @property {string} [displayClass] - Property to style of wrapper of the tab group. Options are "contained" or "full-width"
 * @property {any} onChange - Function to call to set the new active tab
 * @property {{tabId: number, label: string|React.ReactNode}[]} tabLabels - Array of tab label objects. Each object should contain a "tabId" and a "label" property
 * @property {string} [className] - Optional custom user-defined CSS class names, will be appended to the list of any other component classes
 * @property {React.ReactNode} [children] - Optional children
 */

/**
 * LdsTabs component
 * @param {LdsTabsProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element} LdsTabs component
 */

export default function LdsTabs({
  className = '',
  activeTab,
  ariaLabel,
  tabAlign = 'align-left',
  tabClass = 'solid',
  displayClass = 'contained',
  onChange,
  tabLabels = [],
  children,
  ...rest
}) {
  const tabGroupId = useId();
  const tabGroupRef = useRef(null);
  const [forceMobile, setForceMobile] = useState(false);

  // If tab labels are too long for one row in desktop, render the mobile version
  useLayoutEffect(() => {
    function checkWindowSize() {
      const tabs = document.querySelectorAll('.tab-btn-container .tab-btn');
      const tabContainerWidth = tabs[0].closest('.tab-btn-container').offsetWidth;
      let totalTabWidth = 0;
      tabs.forEach((tab) => { totalTabWidth += tab.offsetWidth; });
      if (totalTabWidth > tabContainerWidth) setForceMobile(true);
      else setForceMobile(false);
    }
    checkWindowSize();
    window.addEventListener('resize', checkWindowSize);
    return () => window.removeEventListener('resize', checkWindowSize);
  }, []);

  const handleTabClick = (e, tabId) => {
    onChange(tabId);
  };

  useEffect(() => {
    if (tabGroupRef.current) {
      const tab = tabGroupRef.current.querySelector(`#tab-btn-${activeTab}`);
      if (tab) tab.focus();
    }
  }, [activeTab]);

  return (
    <div
      ref={tabGroupRef}
      className={cn(
        'lds-tab-group',
        displayClass,
        tabClass,
        className
      )}
      id={tabGroupId}
      aria-label={ariaLabel}
      role="application"
      {...rest}
    >
      <div
        className={cn(
          'lds-tab-header',
          {
            [tabAlign]: tabAlign,
          }
        )}
      >
        <DesktopTabButtons
          activeTab={activeTab}
          tabclassName={tabClass}
          displayclassName={displayClass}
          handleTabClick={handleTabClick}
          tabLabels={tabLabels}
          forceMobile={forceMobile}
        />
        <MobileTabButtons
          activeTab={activeTab}
          handleTabClick={handleTabClick}
          tabLabels={tabLabels}
          forceMobile={forceMobile}
          displayclassName={displayClass}
        />
      </div>
      <div
        className={cn(
          'lds-tab-container',
          {
            'tab-padded': displayClass === 'contained',
            wrapper: displayClass === 'full-width',
          }
        )}
      >
        {children}
      </div>
    </div>
  );
}

LdsTabs.propTypes = {
  activeTab: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func.isRequired,
  tabLabels: PropTypes.array.isRequired,
  tabAlign: PropTypes.oneOf(['left', 'right', 'center', 'full']),
  tabClass: PropTypes.oneOf(['solid', 'underlined']),
  displayClass: PropTypes.oneOf(['contained', 'full-width']),
};
